import React, {
  useRef,
  memo,
  useCallback,
  SetStateAction,
  Dispatch,
} from "react";
import styles from "./slider.module.scss";
import {
  CURRENCY_ARRAY_FOR_EURO_OR_USD,
  CURRENCY_ARRAY_FOR_ILS,
} from "../../constants/constants";
import useCurrentCurrency from "components/Pages/Plans/Utils/useCurrentCurrency";
import {
  CurrencyE,
  CurrencySymbolE,
} from "components/Pages/Purchases_new/models/enums";
import { useContent } from "contexts/contentContext/contentContext";

interface SliderI {
  value: number;
  setValue: Dispatch<SetStateAction<number>>;
}

const SLIDER_CIRCLE_WIDTH = 38;

const Slider = memo(({ value, setValue }: SliderI) => {
  const { getTextByKey, getDirection } = useContent();
  const currency = useCurrentCurrency();

  const currentCurrency = CurrencyE[currency as keyof typeof CurrencyE];

  const currentCurrencySymbol =
    CurrencySymbolE[currency as keyof typeof CurrencySymbolE];

  const marks =
    currentCurrency === CurrencyE.ILS
      ? CURRENCY_ARRAY_FOR_ILS
      : CURRENCY_ARRAY_FOR_EURO_OR_USD;

  const sliderRef = useRef<HTMLDivElement>(null);
  const direction = getDirection() === "rtl" ? "right" : "left";

  const updateSlider = useCallback(
    (clientX: number) => {
      if (!sliderRef.current) return;
      const slider = sliderRef.current;
      const rect = slider.getBoundingClientRect();
      const offsetX =
        direction === "left" ? clientX - rect.left : rect.right - clientX;
      const percentage = Math.max(
        0,
        Math.min(100, (offsetX / rect.width) * 100)
      );

      // nearest marks value
      const step = 100 / (marks.length - 1);
      const closestMarkIndex = Math.round(percentage / step);
      setValue(marks[closestMarkIndex]);
    },
    [direction, marks, setValue]
  );

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    updateSlider(event.clientX);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (event: MouseEvent) => {
    updateSlider(event.clientX);
  };

  const handleMouseUp = () => {
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    updateSlider(event.touches[0].clientX);
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 1) return; // Ensure single touch
    updateSlider(event.touches[0].clientX);
  };

  return (
    <div className={styles.wrap}>
      <div
        className={styles.sliderContainer}
        ref={sliderRef}
        onMouseDown={handleMouseDown}
        onTouchMove={handleTouchMove}
        onTouchStart={handleTouchStart}
      >
        <div className={styles.sliderTrack}>
          <div
            className={styles.sliderFill}
            style={{
              width: `${(marks.indexOf(value) / (marks.length - 1)) * 100}%`,
            }}
          ></div>
          <div
            className={styles.sliderThumb}
            style={{
              [direction]: `calc(${
                (marks.indexOf(value) / (marks.length - 1)) * 100
              }% - ${SLIDER_CIRCLE_WIDTH / 2}px)`,
            }}
          >
            <div className={styles.sliderCircle}>{value}</div>
          </div>
          {marks.map((mark, index) => (
            <div
              key={index}
              className={`${styles.sliderMark} ${
                index === 0 || index === marks.length - 1 ? styles.hidden : ""
              }`}
              style={{ [direction]: `${(index / (marks.length - 1)) * 100}%` }}
            ></div>
          ))}
        </div>
      </div>
      <div className={styles.сurrencyAmountWrap}>
        <p>{currentCurrencySymbol}</p>
        <div className={styles.sliderInput}>{value}</div>
        <p className={styles.сurrencyAmountText}>
          {getTextByKey("custom-amount")}
        </p>
      </div>
    </div>
  );
});

export default Slider;
